import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import useExchangeRate from '../hooks/useExchangeRate';

const API = 'https://affinityconcept.tech/api';

const fmt = (n) => `AED ${Math.round(n).toLocaleString('en-GB')}`;

export default function Investments({ token, onLogout }) {
  const [user, setUser] = useState(null);
  const [crypto, setCrypto] = useState([]);
  const [other, setOther] = useState([]);
  const { toAED } = useExchangeRate();

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API}/me`, { headers }).then(r => setUser(r.data));
    axios.get(`${API}/crypto`, { headers }).then(r => setCrypto(r.data)).catch(() => {});
    axios.get(`${API}/other-investments`, { headers }).then(r => setOther(r.data)).catch(() => {});
  }, []);

  // Crypto: current price stored in USD; buy price in holding's own currency
  const cryptoValue = crypto.reduce((s, c) => s + toAED((c.quantity || 0) * (c.current_price || 0), 'USD'), 0);
  const cryptoInvested = crypto.reduce((s, c) => s + toAED((c.quantity || 0) * (c.avg_buy_price || 0), c.currency), 0);
  const cryptoPL = cryptoValue - cryptoInvested;

  const otherValue = other.reduce((s, o) => s + toAED(o.current_value || 0, o.currency), 0);
  const otherInvested = other.reduce((s, o) => s + toAED(o.amount_invested || 0, o.currency), 0);
  const otherPL = otherValue - otherInvested;

  const stocksValue = 0; // IBKR not connected yet
  const grandTotal = stocksValue + cryptoValue + otherValue;
  const totalPL = cryptoPL + otherPL;

  const Card = ({ label, value, sub, accent }) => (
    <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
      <p className="text-xs font-medium text-gray-500 uppercase">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent || 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main className="flex-1 overflow-y-auto p-4 md:p-8 pt-16 md:pt-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Investments</h1>
          <p className="text-gray-500 text-sm">Your full portfolio at a glance</p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <Card label="Stocks" value={fmt(stocksValue)} sub="Not connected" />
          <Card label="Crypto" value={fmt(cryptoValue)} sub={`${crypto.length} coin${crypto.length === 1 ? '' : 's'}`} />
          <Card label="Other" value={fmt(otherValue)} sub={`${other.length} investment${other.length === 1 ? '' : 's'}`} />
          <Card label="Grand Total" value={fmt(grandTotal)} accent="text-green-600" />
        </div>
        <div className="mb-8">
          <Card label="Total P&L (Crypto + Other)" value={`${totalPL >= 0 ? '+' : ''}${fmt(totalPL)}`}
            accent={totalPL >= 0 ? 'text-green-500' : 'text-red-500'} />
        </div>

        {/* Clickable sub-page cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/investments/stocks"
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:border-green-400 hover:shadow-md transition group">
            <div className="text-3xl mb-3">📈</div>
            <h3 className="text-base font-semibold text-gray-900 group-hover:text-green-600">Stocks</h3>
            <p className="text-2xl font-bold text-gray-900 mt-1">{fmt(stocksValue)}</p>
            <span className="inline-block mt-3 text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-600">Coming Soon · IBKR</span>
          </Link>

          <Link to="/investments/crypto"
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:border-green-400 hover:shadow-md transition group">
            <div className="text-3xl mb-3">🪙</div>
            <h3 className="text-base font-semibold text-gray-900 group-hover:text-green-600">Crypto</h3>
            <p className="text-2xl font-bold text-gray-900 mt-1">{fmt(cryptoValue)}</p>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs text-gray-400">{crypto.length} coin{crypto.length === 1 ? '' : 's'}</span>
              <span className={`text-xs font-medium ${cryptoPL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {cryptoPL >= 0 ? '+' : ''}{fmt(cryptoPL)}
              </span>
            </div>
          </Link>

          <Link to="/investments/other"
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:border-green-400 hover:shadow-md transition group">
            <div className="text-3xl mb-3">🏦</div>
            <h3 className="text-base font-semibold text-gray-900 group-hover:text-green-600">Other</h3>
            <p className="text-2xl font-bold text-gray-900 mt-1">{fmt(otherValue)}</p>
            <div className="flex items-center gap-2 mt-3">
              <span className="text-xs text-gray-400">{other.length} investment{other.length === 1 ? '' : 's'}</span>
              <span className={`text-xs font-medium ${otherPL >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                {otherPL >= 0 ? '+' : ''}{fmt(otherPL)}
              </span>
            </div>
          </Link>
        </div>
      </main>
    </div>
  );
}
